import { useMemo } from 'react';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { alpha } from '@mui/material';
import { useGetOne, useRecordContext } from 'react-admin';
import { LinearProgress } from 'shared/mui/LinearProgress/LinearProgress';
import { Typography } from 'shared/mui/Typography';
import { TaskStatusFieldSkeleton } from 'shared/react-admin/Fields/TaskStatusField/TaskStatusFieldSkeleton';
import {
  CommissionRecalculationTask,
  CommissionRecalculationTaskStatus,
} from 'types/embedded/commissionRecalculationTask';
import { getLastUrlSegment } from 'utils';

import { TASK_STATUS_COLORS } from './constants/taskStatusColors';
import { StatusRowStyled, StatusLeftStyled, ProgressWrapperStyled } from './TaskStatusField.styled';

type TaskStatusFieldProps = {
  short?: boolean;
};

export const TaskStatusField: React.FC<TaskStatusFieldProps> = ({ short }) => {
  const record = useRecordContext<CommissionRecalculationTask>();

  const { data, isLoading } = useGetOne('commission_recalculation_statuses', {
    id: record?.status?.['@id'],
  });

  const statusSegment = getLastUrlSegment(record?.status?.['@id']) || 'default';
  const statusColor =
    TASK_STATUS_COLORS[statusSegment as CommissionRecalculationTaskStatus] ??
    TASK_STATUS_COLORS.default;
  const lightStatusColor = alpha(statusColor, 0.2);

  const progressValue = useMemo(() => {
    const total = record?.totalOrdersCount ?? 0;
    const processed = record?.processedOrdersCount ?? 0;

    if (total === 0) {
      return 100;
    }
    return Math.min((processed / total) * 100, 100);
  }, [record]);

  if (isLoading) {
    return <TaskStatusFieldSkeleton />;
  }

  return (
    <>
      <StatusRowStyled>
        <StatusLeftStyled>
          <FiberManualRecordIcon
            fontSize="small"
            sx={{
              color: statusColor,
            }}
          />
          <Typography variant="body2">{data?.description}</Typography>
        </StatusLeftStyled>
        <Typography color="textSecondary" ml="15px" variant="body2">
          {!short ? 'Выполнено:' : ''} {record?.processedOrdersCount} из ~{record?.totalOrdersCount}
        </Typography>
      </StatusRowStyled>
      <ProgressWrapperStyled>
        <LinearProgress
          sx={{
            height: 8,
            backgroundColor: lightStatusColor,
            '& .MuiLinearProgress-bar': {
              backgroundColor: statusColor,
            },
          }}
          value={progressValue}
          variant="determinate"
        />
      </ProgressWrapperStyled>
    </>
  );
};
